import {
  CHECK_HELENA_ENABLED_ERROR,
  CHECK_HELENA_ENABLED_SUCCESS,
  ENABLE_HELENA_ERROR,
  ENABLE_HELENA_SUCCESS,
  HelenaAction,
  PATIENT_ID_HELENA_ERROR,
  PATIENT_ID_HELENA_SUCCESS,
  PATIENT_STATUS_HELENA_SUCCESS,
  REGISTRATION_FINISHED_HELENA_ERROR,
  REGISTRATION_FINISHED_HELENA_SUCCESS,
  REGISTRATION_LINK_HELENA_ERROR,
  REGISTRATION_LINK_HELENA_SUCCESS,
  SESSION_HELENA_ERROR,
  SESSION_HELENA_SUCCESS,
} from "store/types/helena.types";
import {
  Helena,
  HelenaPatientStatusEnum,
  HelenaSessionKey,
  HelenaStatusEnum,
  HelenaType,
} from "model/helena.model";
import {
  CLOSE_PATIENT,
  LOAD_PATIENT_START,
  PatientAction,
} from "store/types/patient.types";
import { HelenaRegistration } from "model/helena-registration.model";
import * as _ from "lodash";
import { GlobalState } from "store/configure-store";
import { selectUserConfiguration } from "./user-context.reducer";

export interface HelenaState extends HelenaType {}

export const initialHelenaState: HelenaState = new Helena(
  HelenaStatusEnum.UP,
  false,
  null,
  null,
  null,
  HelenaPatientStatusEnum.REGISTERED_WITH_ACCOUNT
);

/** ---- SELECTORS ---- */

export const selectHelenaState = (state: GlobalState): HelenaState => {
  return state.helena;
};

export const selectHelenaStatus = (state: HelenaState): HelenaStatusEnum => {
  return state.status;
};

export const selectSessionKey = (state: HelenaState): HelenaSessionKey => {
  return new HelenaSessionKey(state.sessionKey);
};

export const selectHelenaPatientId = (state: HelenaState): string | null => {
  return state.patientId;
};

export const selectHelenaEnabled = (state: HelenaState): boolean => {
  return state.enabled;
};

export const selectHelenaPatientStatus = (
  state: HelenaState
): HelenaPatientStatusEnum => state.patientStatus;

export const selectHelenaPatientUnsubscribed = (
  state: HelenaState
): boolean => {
  return hasHelenaPatientStatus(state, [
    HelenaPatientStatusEnum.EXCLUSION,
    HelenaPatientStatusEnum.DELETED,
    HelenaPatientStatusEnum.BLOCKED_CAREGIVER,
  ]);
};

export const selectHelenaPatientActive = (state: HelenaState): boolean => {
  const rightsToCheck = [
    HelenaPatientStatusEnum.REGISTERED_ADULT,
    HelenaPatientStatusEnum.REGISTERED_CHILD,
    HelenaPatientStatusEnum.REGISTERED_WITH_ACCOUNT,
    HelenaPatientStatusEnum.REGISTERED_WITHOUT_ACCOUNT,
  ];

  return hasHelenaPatientStatus(state, rightsToCheck);
};

export const selectCanUseHelena = (state: HelenaState): boolean => {
  return (
    selectHelenaPatientActive(state) &&
    selectHelenaEnabled(state) &&
    selectHelenaStatus(state) === HelenaStatusEnum.UP
  );
};

export const selectCanUseHelenaForMedication = (
  state: HelenaState
): boolean => {
  return (
    (selectHelenaPatientActive(state) ||
      hasHelenaPatientStatus(state, [
        HelenaPatientStatusEnum.NOT_REGISTERED,
      ])) &&
    selectHelenaEnabled(state) &&
    selectHelenaStatus(state) === HelenaStatusEnum.UP
  );
};

export const selectRegistrationLink = (state: GlobalState): string | null => {
  return state.helena.registrationLink;
};

export const shouldShowHelenaUserActivationPopup = (
  state: GlobalState
): boolean => {
  return (
    !selectHelenaEnabled(state.helena) &&
    selectHelenaStatus(state.helena) === HelenaStatusEnum.UP &&
    !selectUserConfiguration(state).helenaActivationQuestionVisit
  );
};

const hasHelenaPatientStatus = (
  state: HelenaState,
  rightsToCheck: HelenaPatientStatusEnum[]
): boolean => {
  const patientId = state.patientId;
  const patientStatus = state.patientStatus;
  return !!patientId && _.indexOf(rightsToCheck, patientStatus) > -1;
};

/** ---- ACTION HANDLERS ---- */
const closePatient = (state: HelenaState) => ({
  ...state,
  patientId: "",
  status: HelenaStatusEnum.UP,
  registrationLink: "",
});

const helenaEnabled = (state: HelenaState, enabled: boolean) => ({
  ...state,
  status: HelenaStatusEnum.UP,
  enabled: enabled,
});

const sessionHelenaSuccess = (
  state: HelenaState,
  sessionKey: HelenaSessionKey
) => ({
  ...state,
  status: HelenaStatusEnum.UP,
  sessionKey: sessionKey,
});

const patientIdHelenaSuccess = (state: HelenaState, patientId: string) => ({
  ...state,
  status: HelenaStatusEnum.UP,
  patientId: patientId,
});

const registrationLinkHelenaSuccess = (
  state: HelenaState,
  registrationLink: string
) => ({
  ...state,
  status: HelenaStatusEnum.UP,
  registrationLink: registrationLink,
});

const patientStatusHelenaSuccess = (
  state: HelenaState,
  status: HelenaPatientStatusEnum
) => ({
  ...state,
  patientStatus: status,
});

const registrationFinishedHelenaSuccess = (
  state: HelenaState,
  finishedResponse: HelenaRegistration
) => ({
  ...state,
  status: HelenaStatusEnum.UP,
  patientId: finishedResponse.patientId,
});

const helenaStatus = (state: HelenaState, helenaStatus: HelenaStatusEnum) => ({
  ...state,
  status: helenaStatus,
});

/** ---- REDUCER ---- */
export const helenaReducer = (
  state = initialHelenaState,
  action: HelenaAction | PatientAction
): HelenaState => {
  switch (action.type) {
    case CLOSE_PATIENT:
    case LOAD_PATIENT_START:
      return closePatient(state);
    case CHECK_HELENA_ENABLED_SUCCESS:
      return helenaEnabled(state, action.payload);
    case ENABLE_HELENA_SUCCESS:
      return helenaEnabled(state, true);
    case SESSION_HELENA_SUCCESS:
      return sessionHelenaSuccess(state, action.payload);
    case PATIENT_ID_HELENA_SUCCESS:
      return patientIdHelenaSuccess(state, action.payload);
    case REGISTRATION_LINK_HELENA_SUCCESS:
      return registrationLinkHelenaSuccess(state, action.payload);
    case REGISTRATION_FINISHED_HELENA_SUCCESS:
      return registrationFinishedHelenaSuccess(state, action.payload);
    case PATIENT_STATUS_HELENA_SUCCESS:
      return patientStatusHelenaSuccess(state, action.payload);
    case CHECK_HELENA_ENABLED_ERROR:
    case ENABLE_HELENA_ERROR:
    case SESSION_HELENA_ERROR:
    case PATIENT_ID_HELENA_ERROR:
    case REGISTRATION_LINK_HELENA_ERROR:
    case REGISTRATION_FINISHED_HELENA_ERROR:
      return helenaStatus(state, HelenaStatusEnum.DOWN);
    default:
      return state;
  }
};
